/**
 * Phase 3 — RFQ Negotiation drawer.
 * Timeline of negotiation rounds + "Add Round" form, plus the emergency
 * sourcing justification workflow (justify → second-person approval).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Zap, MessageSquareText, Plus, ShieldCheck } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';

const inp = 'w-full text-xs border border-slate-200 rounded-sm px-2 py-1.5 focus:outline-none focus:border-[#1F7A6E]';

export default function RFQNegotiationDrawer({ rfq, onClose, onChanged }) {
  const [log, setLog] = useState([]);
  const [detail, setDetail] = useState(rfq);
  const [form, setForm] = useState(null);
  const [emForm, setEmForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    apiGet(`/rfq/${rfq.id}/negotiation-log`).then((d) => setLog(d?.items || [])).catch(() => {});
    apiGet(`/rfq/${rfq.id}`).then(setDetail).catch(() => {});
  }, [rfq.id]);
  useEffect(() => { reload(); }, [reload]);

  const vendors = detail?.vendors?.length ? detail.vendors : (detail?.vendor_ids || []);
  const isEmergency = detail?.sourcing_route === 'emergency';
  const emApproved = !!detail?.justification_approved_by;

  const addRound = async () => {
    if (!form.vendor_name) { toast.error('Pick the vendor for this round'); return; }
    setBusy(true);
    try {
      await apiPost(`/rfq/${rfq.id}/negotiation-round`, {
        vendor_name: form.vendor_name,
        vendor_id: form.vendor_name,
        counter_offer_notes: form.notes || '',
        revised_rates: form.revised_rate ? [{ line_item_id: 'all', revised_rate: Number(form.revised_rate) }] : [],
      });
      toast.success('Negotiation round logged');
      setForm(null); reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const justifyEmergency = async () => {
    if (!emForm.justification?.trim()) { toast.error('Justification is required'); return; }
    setBusy(true);
    try {
      await apiPost(`/rfq/${rfq.id}/justify-emergency`, emForm);
      toast.success('Flagged as emergency purchase — needs one extra approval before award');
      setEmForm(null); reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const approveJustification = async () => {
    setBusy(true);
    try {
      await apiPost(`/rfq/${rfq.id}/approve-emergency-justification`);
      toast.success('Emergency justification approved');
      reload(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="rfq-negotiation-drawer">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <MessageSquareText size={16} className="text-[#1F7A6E]" /> Negotiation · {detail?.rfq_no || rfq.rfq_no}
          </SheetTitle>
          <SheetDescription>{detail?.item || detail?.project_name || ''}</SheetDescription>
        </SheetHeader>

        {/* Emergency section */}
        <div className="mt-4 border border-slate-200 rounded-sm p-3 bg-slate-50" data-testid="rfq-emergency-section">
          {isEmergency ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800 rounded-sm font-display font-bold uppercase">⚡ Emergency</span>
                {emApproved ? (
                  <span className="px-1.5 py-0.5 text-[10px] bg-emerald-50 text-emerald-700 rounded-sm font-display font-bold uppercase" data-testid="rfq-em-approved-badge">Justification approved</span>
                ) : (
                  <span className="px-1.5 py-0.5 text-[10px] bg-red-50 text-red-700 rounded-sm font-display font-bold uppercase">Awaiting justification approval</span>
                )}
              </div>
              <p className="text-xs text-slate-700">{detail?.justification}</p>
              {detail?.single_vendor_reason && <p className="text-[11px] text-slate-500">Single vendor: {detail.single_vendor_reason}</p>}
              <p className="text-[10px] text-slate-400">Flagged by {detail?.justified_by} · {formatDate(detail?.justified_at)}</p>
              {emApproved ? (
                <p className="text-[10px] text-emerald-600 flex items-center gap-1"><ShieldCheck size={11} /> Approved by {detail.justification_approved_by} · {formatDate(detail.justification_approved_at)}</p>
              ) : (
                <button onClick={approveJustification} disabled={busy} className="mt-1 px-2.5 py-1.5 text-xs bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="rfq-em-approve-btn">
                  Approve Justification
                </button>
              )}
            </div>
          ) : emForm ? (
            <div className="space-y-2" data-testid="rfq-em-form">
              <div className="kpi-overline flex items-center gap-1"><Zap size={11} /> Flag as Emergency / Spot Purchase</div>
              <textarea placeholder="Justification * (e.g. plant breakdown, safety-critical)" value={emForm.justification || ''}
                onChange={(e) => setEmForm({ ...emForm, justification: e.target.value })} className={`${inp} h-16`} data-testid="rfq-em-justification" />
              <input placeholder="Single-vendor reason (if applicable)" value={emForm.single_vendor_reason || ''}
                onChange={(e) => setEmForm({ ...emForm, single_vendor_reason: e.target.value })} className={inp} />
              <div className="flex gap-2">
                <button onClick={justifyEmergency} disabled={busy} className="px-2.5 py-1.5 text-xs bg-amber-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="rfq-em-save">Flag Emergency</button>
                <button onClick={() => setEmForm(null)} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEmForm({})} className="flex items-center gap-1.5 text-xs text-amber-700 font-display font-bold" data-testid="rfq-em-flag-btn">
              <Zap size={12} /> Flag as emergency / spot purchase
            </button>
          )}
        </div>

        {/* Negotiation timeline */}
        <div className="mt-4">
          <div className="kpi-overline mb-2">Negotiation Rounds ({log.length})</div>
          {log.length === 0 && <div className="text-xs text-slate-400 py-4 text-center">No rounds logged yet</div>}
          <div className="space-y-2">
            {log.map((r) => (
              <div key={r.id} className="border-l-2 border-[#1F7A6E] pl-3 py-1" data-testid={`rfq-neg-round-${r.round_no}`}>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-display font-bold text-[#1F7A6E]">Round {r.round_no}</span>
                  <span className="text-slate-600">{r.vendor_name}</span>
                  <span className="text-slate-400 text-[10px] ml-auto">{formatDate(r.logged_at)} · {r.logged_by}</span>
                </div>
                {r.counter_offer_notes && <p className="text-xs text-slate-700 mt-0.5">{r.counter_offer_notes}</p>}
                {(r.revised_rates || []).map((rr, i) => (
                  <span key={i} className="inline-block mt-1 px-1.5 py-0.5 text-[10px] bg-slate-100 rounded-sm tabular-nums">Revised rate: ₹{Number(rr.revised_rate).toLocaleString('en-IN')}</span>
                ))}
              </div>
            ))}
          </div>

          {form ? (
            <div className="mt-3 border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2" data-testid="rfq-neg-form">
              <select value={form.vendor_name || ''} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} className={inp} data-testid="rfq-neg-vendor">
                <option value="">— Vendor —</option>
                {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <textarea placeholder="Counter-offer notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inp} h-14`} data-testid="rfq-neg-notes" />
              <input type="number" placeholder="Revised rate (optional)" value={form.revised_rate || ''} onChange={(e) => setForm({ ...form, revised_rate: e.target.value })} className={inp} />
              <div className="flex gap-2">
                <button onClick={addRound} disabled={busy} className="px-2.5 py-1.5 text-xs bg-[#1F7A6E] text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid="rfq-neg-save">Log Round</button>
                <button onClick={() => setForm(null)} className="px-2.5 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setForm({})} className="mt-3 flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#1F7A6E] text-white rounded-sm font-display font-bold" data-testid="rfq-neg-add-btn">
              <Plus size={11} /> Add Round
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
